import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { HighlightOff as DeleteIcon } from '@material-ui/icons';
import Spinner from 'common/components/spinner/spinner';
import { format, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import InviteGuestModal from './components/inviteGuestModal';
import ConfirmDeleteModal from './components/confirmDeleteModal';
import { listMyGuests, revokeGuest } from './guestsActions';

const PRIMARY_COLOR = '#5759FB';
const PRIMARY_COLOR_HOVER = '#4547dc';
const DELETE_COLOR = '#e53935';

const STATUS_PILL = {
  pending: { label: 'Aguardando aceite', bg: '#fff4d6', color: '#8a5a00' },
  accepted: { label: 'Ativo', bg: '#e0f5e1', color: '#1f7a2c' },
  expired: { label: 'Expirado', bg: '#eeeeee', color: '#666666' },
  revoked: { label: 'Revogado', bg: '#eeeeee', color: '#666666' },
};

const ACTIVE_STATUSES = ['pending', 'accepted'];
const HISTORY_STATUSES = ['expired', 'revoked'];

const useStyles = makeStyles(() => ({
  ctaButton: {
    backgroundColor: `${PRIMARY_COLOR} !important`,
    color: '#ffffff !important',
    borderRadius: '4px !important',
    padding: '8px 20px !important',
    textTransform: 'none !important',
    boxShadow: 'none !important',
    '&:hover': { backgroundColor: `${PRIMARY_COLOR_HOVER} !important` },
    '&:focus': { outline: 'none !important' },
    '&::-moz-focus-inner': { border: 0 },
    '&.Mui-disabled': {
      backgroundColor: '#c0c0d8 !important',
      color: '#ffffff !important',
    },
  },
  historyToggle: {
    color: `${PRIMARY_COLOR} !important`,
    textTransform: 'none !important',
    '&:focus': { outline: 'none !important' },
    '&::-moz-focus-inner': { border: 0 },
    '&:hover': { backgroundColor: 'rgba(87, 89, 251, 0.04) !important' },
  },
  capacityChip: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    color: '#333',
    fontWeight: 500,
  },
  deleteIcon: {
    color: `${DELETE_COLOR} !important`,
    fontSize: 26,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
  },
}));

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'dd/MM/yyyy');
  } catch (_e) {
    return '-';
  }
};

const StatusPill = ({ status }) => {
  const meta = STATUS_PILL[status] || {
    label: status,
    bg: '#eee',
    color: '#666',
  };
  return (
    <Chip
      label={meta.label}
      size="small"
      style={{
        backgroundColor: meta.bg,
        color: meta.color,
        fontWeight: 500,
        height: 24,
      }}
    />
  );
};

const actionLabelFor = (status) => {
  if (status === 'pending') return 'Cancelar convite';
  if (status === 'accepted') return 'Revogar acesso';
  return null;
};

function GuestSection({
  title,
  capacityLabel,
  ctaLabel,
  onCta,
  ctaDisabled,
  ctaDisabledTooltip,
  items,
  loading,
  revokeLoading,
  showHistory,
  onToggleHistory,
  onAskDelete,
  classes,
  emptyText,
}) {
  const active = items.filter((g) => ACTIVE_STATUSES.includes(g.status));
  const history = items.filter((g) => HISTORY_STATUSES.includes(g.status));
  const visible = showHistory ? [...active, ...history] : active;

  return (
    <Box mb={4}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
        flexWrap="wrap"
        gridGap={8}
      >
        <Box display="flex" alignItems="center" gridGap={12}>
          <Typography className={classes.sectionTitle}>{title}</Typography>
          {capacityLabel && (
            <Chip
              label={capacityLabel}
              size="small"
              className={classes.capacityChip}
            />
          )}
        </Box>
        {ctaDisabled && ctaDisabledTooltip ? (
          <Tooltip title={ctaDisabledTooltip}>
            <span>
              <Button
                variant="contained"
                onClick={onCta}
                className={classes.ctaButton}
                disabled
              >
                {ctaLabel}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            onClick={onCta}
            className={classes.ctaButton}
            disabled={ctaDisabled}
          >
            {ctaLabel}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Convidado em</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 32 }}>
                  <Spinner />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: 24 }}>
                  <Typography variant="body2" color="textSecondary">
                    {emptyText}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((g) => {
                const actionLabel = actionLabelFor(g.status);
                return (
                  <TableRow key={g.id}>
                    <TableCell>{g.name}</TableCell>
                    <TableCell>{formatDate(g.invited_at)}</TableCell>
                    <TableCell>
                      <StatusPill status={g.status} />
                    </TableCell>
                    <TableCell align="right">
                      {actionLabel && (
                        <Tooltip title={actionLabel}>
                          <span>
                            <IconButton
                              aria-label={actionLabel}
                              onClick={() => onAskDelete(g)}
                              disabled={revokeLoading}
                              size="small"
                            >
                              <DeleteIcon className={classes.deleteIcon} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {history.length > 0 && (
        <Box mt={1} display="flex" justifyContent="flex-end">
          <Button
            size="small"
            onClick={onToggleHistory}
            className={classes.historyToggle}
          >
            {showHistory
              ? 'Ocultar histórico'
              : `Mostrar histórico (${history.length})`}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function Guests({ auth, guests, listMyGuests, revokeGuest, history }) {
  const classes = useStyles();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [initialType, setInitialType] = useState('friend');
  const [toDelete, setToDelete] = useState(null);
  const [showFriendHistory, setShowFriendHistory] = useState(false);
  const [showDependentHistory, setShowDependentHistory] = useState(false);

  // Convidado (User com guest_id) não pode convidar — espelha o gate do
  // backend (ensureInviterActive lança inviter_is_guest 403). Manda pra home
  // se cair aqui via URL direta.
  const isGuestUser = !!auth?.user?.guest_id;
  const featureDisabled = !!guests?.featureDisabled;

  useEffect(() => {
    if (isGuestUser) {
      if (history?.replace) history.replace('/homepage');
      return;
    }
    listMyGuests();
  }, [isGuestUser, listMyGuests, history]);

  useEffect(() => {
    if (featureDisabled && history?.replace) history.replace('/homepage');
  }, [featureDisabled, history]);

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    revokeGuest(toDelete.id, {
      onSuccess: () => setToDelete(null),
    });
  };

  const openInvite = (type) => {
    setInitialType(type);
    setInviteOpen(true);
  };

  const items = guests?.list?.items || [];
  const meta = guests?.list?.meta || {
    friend_limit: 0,
    friend: { pending: 0, accepted: 0, expired: 0, revoked: 0 },
    dependent: { pending: 0, accepted: 0, expired: 0, revoked: 0 },
  };

  const friends = useMemo(
    () => items.filter((g) => g.guest_type === 'friend'),
    [items]
  );
  const dependents = useMemo(
    () => items.filter((g) => g.guest_type === 'dependent'),
    [items]
  );

  const friendUsed = meta.friend.accepted || 0;
  const friendCapacityLabel = meta.friend_limit
    ? `${friendUsed}/${meta.friend_limit} vagas`
    : null;
  const friendLimitReached =
    !!meta.friend_limit && friendUsed >= meta.friend_limit;
  const dependentActive =
    (meta.dependent.accepted || 0) + (meta.dependent.pending || 0);
  const dependentCapacityLabel = `${dependentActive} ${
    dependentActive === 1 ? 'convidado' : 'convidados'
  }`;

  if (isGuestUser || featureDisabled) return null;

  return (
    <section
      style={{
        backgroundColor: 'transparent',
        minHeight: '100vh',
        padding: 16,
      }}
    >
      <Box mb={3}>
        <Typography variant="h4">Meus convidados</Typography>
      </Box>

      <GuestSection
        title="Amigos"
        capacityLabel={friendCapacityLabel}
        ctaLabel="Convidar amigo"
        onCta={() => openInvite('friend')}
        ctaDisabled={friendLimitReached}
        ctaDisabledTooltip="Limite de amigos cadastrados atingido. Para liberar vaga, revogue o acesso de algum amigo já cadastrado."
        items={friends}
        loading={guests.loading}
        revokeLoading={guests.revokeLoading}
        showHistory={showFriendHistory}
        onToggleHistory={() => setShowFriendHistory((v) => !v)}
        onAskDelete={setToDelete}
        classes={classes}
        emptyText="Você ainda não convidou nenhum amigo."
      />

      <GuestSection
        title="Dependentes"
        capacityLabel={dependentCapacityLabel}
        ctaLabel="Convidar dependente"
        onCta={() => openInvite('dependent')}
        items={dependents}
        loading={guests.loading}
        revokeLoading={guests.revokeLoading}
        showHistory={showDependentHistory}
        onToggleHistory={() => setShowDependentHistory((v) => !v)}
        onAskDelete={setToDelete}
        classes={classes}
        emptyText="Você ainda não convidou nenhum dependente."
      />

      <InviteGuestModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        initialType={initialType}
        inviterUserId={auth?.user?.id}
      />

      <ConfirmDeleteModal
        open={!!toDelete}
        guestName={toDelete?.name}
        loading={guests.revokeLoading}
        onCancel={() => setToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </section>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  guests: state.guests,
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ listMyGuests, revokeGuest }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Guests);
